import type { AiModel } from '../api/aiModels';

export type PriceSymbol = '' | '$' | '$$' | '$$$' | '$$$$' | '$$$$$';
export interface ModelPriceDisplay {
  kind: 'free' | 'priced' | 'unavailable';
  symbol: PriceSymbol;
  label: string;
  description: string;
  provenance: string;
}
// Affordability bands over the combined catalog price per 1M tokens (prompt + completion), the one
// source both clients share: $ under 1, $$ 1 to under 5, $$$ 5 to under 20, $$$$ 20 to under 60,
// $$$$$ 60 and up. Conditional/base rates band the same way; a zero-priced but unverified row bands
// as $ so nothing but verified-free claims free.
export function priceSymbol(promptUsdPerToken: number, completionUsdPerToken: number): PriceSymbol {
  const combined = (promptUsdPerToken + completionUsdPerToken) * 1e6;
  return combined >= 60 ? '$$$$$' : combined >= 20 ? '$$$$' : combined >= 5 ? '$$$' : combined >= 1 ? '$$' : '$';
}
// Shift the decimal representation instead of multiplying floats (1e-7 × 1e6 must display 0.1).
function perMillion(rate: number): string {
  const [coefficient, exponent = '0'] = String(rate).split('e');
  const [whole, fraction = ''] = coefficient.split('.');
  const digits = whole + fraction, point = whole.length + Number(exponent) + 6;
  const scaled = point <= 0 ? `0.${'0'.repeat(-point)}${digits}`
    : point >= digits.length ? digits.padEnd(point, '0') : `${digits.slice(0, point)}.${digits.slice(point)}`;
  const [integer, decimals = ''] = scaled.split('.');
  const decimal = decimals.replace(/0+$/, '');
  return integer.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (decimal ? `.${decimal}` : '');
}
export function modelPricing(model: Pick<AiModel, 'provider' | 'free' | 'pricing'>): ModelPriceDisplay {
  const pricing = model.pricing;
  const rate = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  if (!pricing || model.provider !== 'openrouter' || pricing.source !== 'openrouter' || pricing.currency !== 'USD'
    || !rate(pricing.prompt_usd_per_token) || !rate(pricing.completion_usd_per_token)
    || typeof pricing.conditional !== 'boolean'
    || typeof pricing.observed_at !== 'string' || !/^\d{4}-\d{2}-\d{2}T/.test(pricing.observed_at) || !Number.isFinite(Date.parse(pricing.observed_at))) {
    return { kind: 'unavailable', symbol: '', label: 'Pricing unavailable', description: 'Pricing unavailable.', provenance: '' };
  }
  const provenance = `OpenRouter catalog, observed ${pricing.observed_at}.`;
  if (model.free === true && pricing.prompt_usd_per_token === 0 && pricing.completion_usd_per_token === 0 && !pricing.conditional) {
    return { kind: 'free', symbol: '', label: '(free)', description: 'Free: verified zero catalog charges.', provenance };
  }
  const input = perMillion(pricing.prompt_usd_per_token), output = perMillion(pricing.completion_usd_per_token);
  return {
    kind: 'priced',
    symbol: priceSymbol(pricing.prompt_usd_per_token, pricing.completion_usd_per_token),
    label: `USD $${input} input · $${output} output /1M tokens${pricing.conditional ? ' · Base rates' : ''}`,
    description: `Catalog base prices: USD ${input} per 1 million input tokens; USD ${output} per 1 million output tokens.${pricing.conditional ? ' Conditional pricing applies.' : ''} Additional usage charges may apply.`,
    provenance,
  };
}
